import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { HelmPlanFinder } from "@/components/case/HelmPlanFinder";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import HouseholdDocuments from "@/pages/agent/HouseholdDocuments";
import SunfireEnrollments from "@/pages/agent/SunfireEnrollments";
import HouseholdEnrollment from "@/pages/agent/HouseholdEnrollment";
import { CallPilot } from "@/components/helm/CallPilot";
import { HouseholdTextButton } from "@/components/helm/HouseholdTextPanel";
import { HouseholdProfileEditor, CandidatePlansEditor, SaveState } from "@/components/helm/HouseholdEditor";
import { PrimaryContactEdit } from "@/components/helm/PrimaryContactEdit";
import { RemoveHouseholdButton } from "@/components/helm/RemoveHouseholdButton";
import { MyDoctors } from "@/components/MyDoctors";
import { MyMedications } from "@/components/MyMedications";
import { PlanComparePanel } from "@/components/PlanComparePanel";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, User, ShieldCheck, ClipboardList, Clock, ListTodo, FileText,
  Phone, Mail, Plus, Check, AlarmClock, CircleUser, Search, Compass, Send, ChevronDown, Info as InfoIcon, Loader2,
  History, Pencil, Repeat, AlertTriangle, Navigation, Flag, ArrowRight, MessageSquare, BookMarked,
} from "lucide-react";

const LABEL = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const PRIO = { high: "bg-[#FEE2E2] text-[#991B1B]", medium: "bg-[#FEF3C7] text-[#92400E]", low: "bg-[#EEF2F6] text-[#1E3E62]" };
const ACT_ICON = {
  note: FileText, task: ListTodo, assessment: ClipboardList, enrollment: ShieldCheck,
  status_change: Clock, system: CircleUser, document: FileText, call: Phone,
};
const REL_LABEL = {
  visitor: "Visitor", registered_household: "Registered household",
  advisor_prospect: "Advisor prospect", active_client: "Active client",
};

export default function HouseholdProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [askCtx, setAskCtx] = useState(null);
  const [rel, setRel] = useState(null);
  const [tab, setTab] = useState(searchParams.get("tab") || "overview");

  const load = async () => {
    try {
      const { data } = await api.get(`/agent/households/${id}`);
      setD(data);
    } catch (e) {
      setErr(e?.response?.status === 403 ? "You don't have access to this household." : "Household not found.");
    }
  };
  const loadAskCtx = async () => {
    try { const { data } = await api.get(`/agent/households/${id}/ask-context`); setAskCtx(data); }
    catch { setAskCtx(null); }
  };
  const loadRel = async () => {
    try { const { data } = await api.get(`/agent/households/${id}/enrollment`); setRel(data); }
    catch { setRel(null); }
  };
  useEffect(() => { load(); loadAskCtx(); loadRel(); /* eslint-disable-next-line */ }, [id]);

  if (err) return (
    <div className="p-6 sm:p-10 max-w-4xl mx-auto" data-testid="household-profile-error">
      <Button variant="ghost" onClick={() => navigate("/agent")} className="mb-4"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
      <div className="rounded-xl border-2 border-[#F3D6D6] bg-[#FDF3F3] p-8 text-center text-[#991B1B] font-semibold">{err}</div>
    </div>
  );
  if (!d) return <div className="p-10 max-w-4xl mx-auto"><Skeleton className="h-96 w-full rounded-xl" /></div>;

  const c = d.card;
  return (
    <div className="p-6 sm:p-10 max-w-5xl mx-auto" data-testid="household-profile">
      <Button variant="ghost" onClick={() => navigate("/agent")} className="mb-3" data-testid="profile-back"><ArrowLeft className="w-4 h-4 mr-1" /> All clients</Button>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-full bg-[#0B192C] text-white grid place-items-center font-display text-lg">{(c.name || "?").slice(0, 1)}</div>
        <div>
          <h1 className="font-display text-3xl text-[#0B192C] leading-tight" data-testid="profile-name">{c.name}</h1>
          <p className="text-sm text-[#6B7280]">{c.state || "—"} · <span className="font-semibold">{LABEL(c.lifecycle)}</span></p>
        </div>
        <span className="ml-auto flex flex-wrap items-center gap-2">
          {c.is_test && <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-[#FEF3C7] text-[#92400E]" data-testid="profile-test-badge">TEST · not a prospect</span>}
          {c.archived && <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-[#FEE2E2] text-[#991B1B]" data-testid="profile-removed-badge">Removed from workspace</span>}
          <RemoveHouseholdButton hid={id} name={c.name} archived={c.archived} onDone={load} />
          <HouseholdTextButton householdId={id} />
          <Button size="sm" variant="outline" className="rounded-full px-4" data-testid="household-call-btn"
            onClick={() => { setTab("overview"); setTimeout(() => document.getElementById("household-callpilot")?.scrollIntoView({ behavior: "smooth" }), 50); }}>
            <Phone className="w-4 h-4 mr-1.5" /> Call
          </Button>
          {rel && <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-[#0B192C] text-[#D4AF37]" data-testid="profile-relationship-badge">{REL_LABEL[rel.relationship_status_effective] || rel.relationship_status_effective}{rel.imported_sba_client ? " · SBA" : ""}</span>}
          <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-[#EEF2F6] text-[#1E3E62]">{c.claimed ? "Registered account" : "Guest"}</span>
        </span>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto" data-testid="profile-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview"><User className="w-4 h-4 mr-1.5" />Overview</TabsTrigger>
          <TabsTrigger value="coverage" data-testid="tab-coverage"><ShieldCheck className="w-4 h-4 mr-1.5" />Coverage</TabsTrigger>
          <TabsTrigger value="enrollment" data-testid="tab-enrollment"><Send className="w-4 h-4 mr-1.5" />Enrollment</TabsTrigger>
          <TabsTrigger value="ask" data-testid="tab-ask"><Compass className="w-4 h-4 mr-1.5" />Ask Nauticus</TabsTrigger>
          <TabsTrigger value="finder" data-testid="tab-plan-finder"><Compass className="w-4 h-4 mr-1.5" />Plan finder</TabsTrigger>
          <TabsTrigger value="journey" data-testid="tab-journey"><Navigation className="w-4 h-4 mr-1.5" />Journey</TabsTrigger>
          <TabsTrigger value="needs" data-testid="tab-needs"><ClipboardList className="w-4 h-4 mr-1.5" />Needs</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity"><Clock className="w-4 h-4 mr-1.5" />Activity</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="tab-tasks"><ListTodo className="w-4 h-4 mr-1.5" />Tasks</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents"><FileText className="w-4 h-4 mr-1.5" />Documents</TabsTrigger>
        </TabsList>

        <TabsContent value="overview"><div className="mb-4" id="household-callpilot"><CallPilot householdId={id} /></div><Overview d={d} hid={id} reload={load} askCtx={askCtx} onOpenJourney={() => setTab("journey")} /><HouseholdProfileEditor hid={id} onSaved={load} /></TabsContent>
        <TabsContent value="coverage"><Coverage d={d} hid={id} reload={load} /></TabsContent>
        <TabsContent value="enrollment"><HouseholdEnrollment hid={id} onChanged={loadRel} /></TabsContent>
        <TabsContent value="ask"><CoPilot hid={id} householdName={c.name} county={d.person?.county} usState={d.person?.state || c.state} memberEmail={d.person?.email} /></TabsContent>
        <TabsContent value="finder"><HelmPlanFinder householdId={id} /></TabsContent>
        <TabsContent value="journey"><JourneyContext ctx={askCtx} hid={id} reload={loadAskCtx} /></TabsContent>
        <TabsContent value="needs"><Needs d={d} hid={id} reload={load} /><HouseholdTexts hid={id} /></TabsContent>
        <TabsContent value="activity"><Activity hid={id} /></TabsContent>
        <TabsContent value="tasks"><TasksTab hid={id} /></TabsContent>
        <TabsContent value="documents"><Documents hid={id} /></TabsContent>
      </Tabs>
    </div>
  );
}

function Card({ children }) { return <div className="rounded-xl border-2 border-[#E5E5E0] bg-white p-6 mt-4">{children}</div>; }
function Info({ label, value }) { return <div><p className="text-xs text-[#6B7280]">{label}</p><p className="font-semibold text-[#0B192C]">{value || "—"}</p></div>; }

function Overview({ d, hid, reload, askCtx, onOpenJourney }) {
  const c = d.card, p = d.person || {};
  const ho = askCtx?.handoff;
  const na = askCtx?.next_action;
  return (
    <Card>
      {(ho || na) && (
        <div className="mb-5 rounded-xl border-2 border-[#CFE0F5] bg-[#F3F8FF] p-4" data-testid="overview-journey-banner">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="space-y-2">
              {ho && (
                <div className="flex items-start gap-2" data-testid="overview-handoff-reason">
                  <Flag className="w-4 h-4 text-[#1E3E62] mt-0.5 shrink-0" />
                  <p className="text-sm text-[#0B192C]"><span className="font-bold">Handoff reason:</span> {ho.reason}</p>
                </div>
              )}
              {na && (
                <div className="flex items-start gap-2" data-testid="overview-next-action">
                  <ArrowRight className="w-4 h-4 text-[#1E3E62] mt-0.5 shrink-0" />
                  <p className="text-sm text-[#0B192C]"><span className="font-bold">Next action:</span> {na.label}{na.detail ? <span className="text-[#6B7280]"> — {na.detail}</span> : null}</p>
                </div>
              )}
            </div>
            <Button size="sm" variant="outline" onClick={onOpenJourney} data-testid="overview-open-journey"><Navigation className="w-4 h-4 mr-1.5" />Open Journey</Button>
          </div>
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-5">
        <Info label="Primary contact" value={`${p.first_name || ""} ${p.last_name || ""}`.trim()} />
        <Info label="Lifecycle" value={LABEL(c.lifecycle)} />
        <Info label="Phone" value={p.phone} />
        <Info label="Email" value={p.email} />
        <PrimaryContactEdit hid={hid} person={d.person} choices={d.contact_choices} onSaved={reload} />
        <Info label="County" value={p.county ? `${p.county} County` : undefined} testid="profile-county" />
        <Info label="State" value={c.state} />
        <Info label="Acquisition" value={c.acquisition_source} />
        <Info label="Assigned advisor" value={d.assigned_producer?.name || "Unassigned"} />
        <Info label="Account" value={c.claimed ? "Registered" : "Guest"} />
      </div>
      {c.requested_contact && c.contact && (
        <div className="mt-5 rounded-xl border-2 border-[#CDE7D8] bg-[#F0F7F4] p-4" data-testid="profile-permitted-contact">
          <p className="text-sm font-bold text-[#1F3D2F] mb-1">Consumer requested advisor help — permitted contact</p>
          <p className="text-sm flex gap-4">
            {c.contact.phone && <span><Phone className="w-3.5 h-3.5 inline mr-1" />{c.contact.phone}</span>}
            {c.contact.email && <span><Mail className="w-3.5 h-3.5 inline mr-1" />{c.contact.email}</span>}
          </p>
        </div>
      )}
    </Card>
  );
}

function Coverage({ d, hid, reload }) {
  const list = d.coverages || [];
  const ctx = d.coverage_context || {};
  const [attachFor, setAttachFor] = useState(null);   // coverage id to attach/change CMS identity
  const [replaceFor, setReplaceFor] = useState(null);  // coverage row to replace
  const [editFor, setEditFor] = useState(null);        // coverage row to edit details
  const [verifyFor, setVerifyFor] = useState(null);    // coverage row for external (Sunfire) attestation
  const [bookFor, setBookFor] = useState(null);        // coverage row for book ("business I wrote") attestation

  if (list.length === 0) {
    return (
      <Card>
        <h3 className="font-bold text-[#0B192C] mb-3">Medicare coverage</h3>
        <p className="text-sm text-[#6B7280]" data-testid="profile-coverage-empty">No coverage on record yet. Record an enrollment from the workspace drawer.</p>
      </Card>
    );
  }

  const current = list.find((c) => c.is_ask_context) || null;
  const otherCurrent = list.filter((c) => !c.is_ask_context && c.lifecycle_state === "current");
  const pending = list.filter((c) => !c.is_ask_context && (c.lifecycle_state === "future" || c.lifecycle_state === "pending"));
  const ambiguousRows = list.filter((c) => c.lifecycle_state === "ambiguous");
  const history = list.filter((c) => c.lifecycle_state === "prior");

  return (
    <div className="space-y-4" data-testid="coverage-tab">
      {ctx.ambiguous_current && (
        <div className="rounded-xl border-2 border-[#FDE68A] bg-[#FFFBEB] p-4 mt-4 flex gap-3" data-testid="coverage-ambiguous-banner">
          <AlertTriangle className="w-5 h-5 text-[#92400E] shrink-0" />
          <div>
            <p className="font-bold text-[#92400E] text-sm">Multiple conflicting current plans</p>
            <p className="text-sm text-[#92400E]">This household has more than one in-force plan with different identities. Ask Nauticus won't guess — resolve which plan is current by replacing or ending the others.</p>
          </div>
        </div>
      )}

      {current && <CoverageCard c={current} prominent hid={hid}
        onAttach={() => setAttachFor(current.id)} onReplace={() => setReplaceFor(current)} onEdit={() => setEditFor(current)} onVerifyExternal={() => setVerifyFor(current)} onBook={() => setBookFor(current)} />}

      {ambiguousRows.map((c) => (
        <CoverageCard key={c.id} c={c} hid={hid} conflict
          onAttach={() => setAttachFor(c.id)} onReplace={() => setReplaceFor(c)} onEdit={() => setEditFor(c)} onVerifyExternal={() => setVerifyFor(c)} onBook={() => setBookFor(c)} />
      ))}

      {otherCurrent.length > 0 && (
        <div data-testid="coverage-other-members">
          <p className="text-xs font-bold text-[#6B7280] uppercase tracking-wide mt-2 mb-1 px-1">Other household member coverage</p>
          {otherCurrent.map((c) => <CoverageCard key={c.id} c={c} hid={hid}
            onAttach={() => setAttachFor(c.id)} onReplace={() => setReplaceFor(c)} onEdit={() => setEditFor(c)} onVerifyExternal={() => setVerifyFor(c)} onBook={() => setBookFor(c)} />)}
        </div>
      )}

      {pending.length > 0 && (
        <div data-testid="coverage-pending">
          <p className="text-xs font-bold text-[#6B7280] uppercase tracking-wide mt-2 mb-1 px-1">Pending / upcoming</p>
          {pending.map((c) => <CoverageCard key={c.id} c={c} hid={hid}
            onAttach={() => setAttachFor(c.id)} onEdit={() => setEditFor(c)} onBook={() => setBookFor(c)} />)}
        </div>
      )}

      {history.length > 0 && (
        <details className="rounded-xl border-2 border-[#E5E5E0] bg-white" data-testid="coverage-history">
          <summary className="cursor-pointer select-none px-6 py-4 font-bold text-[#0B192C] flex items-center gap-2">
            <History className="w-4 h-4 text-[#6B7280]" />Coverage history <span className="text-xs font-semibold text-[#6B7280]">({history.length})</span>
          </summary>
          <div className="px-4 pb-4 space-y-3">
            {history.map((c) => <CoverageCard key={c.id} c={c} hid={hid} historical />)}
          </div>
        </details>
      )}

      {attachFor && (
        <AttachCmsDialog open={!!attachFor} onOpenChange={(v) => !v && setAttachFor(null)} hid={hid}
          coverageId={attachFor} cov={list.find((c) => c.id === attachFor)}
          onSaved={() => { setAttachFor(null); reload(); }} />
      )}
      {replaceFor && (
        <ReplaceCoverageDialog open={!!replaceFor} onOpenChange={(v) => !v && setReplaceFor(null)} hid={hid}
          prior={replaceFor} onSaved={() => { setReplaceFor(null); reload(); }} />
      )}
      {editFor && (
        <EditCoverageDialog open={!!editFor} onOpenChange={(v) => !v && setEditFor(null)} hid={hid}
          cov={editFor} onSaved={() => { setEditFor(null); reload(); }} />
      )}
      {verifyFor && (
        <VerifyExternalDialog open={!!verifyFor} onOpenChange={(v) => !v && setVerifyFor(null)} hid={hid}
          cov={verifyFor} onSaved={() => { setVerifyFor(null); reload(); }} />
      )}
      {bookFor && (
        <BookAttestationDialog open={!!bookFor} onOpenChange={(v) => !v && setBookFor(null)} hid={hid}
          cov={bookFor} onSaved={() => { setBookFor(null); reload(); }} />
      )}
    </div>
  );
}

const STATE_PILL = {
  current: "bg-[#DCFCE7] text-[#166534]", pending: "bg-[#FEF3C7] text-[#92400E]",
  future: "bg-[#E0F2FE] text-[#075985]", prior: "bg-[#F1F5F9] text-[#475569]",
  ambiguous: "bg-[#FEE2E2] text-[#991B1B]",
};
const STATE_TXT = { current: "Current", pending: "Pending", future: "Upcoming", prior: "Prior", ambiguous: "Conflicting current" };

function CoverageCard({ c, prominent, historical, conflict, hid, onAttach, onReplace, onEdit, onVerifyExternal, onBook }) {
  const v = c.verified_identity;
  const desc = c.descriptive || {};
  const conflicts = c.descriptive_conflicts || [];
  const av = c.advisor_verified;
  const book = c.book_attestation;
  return (
    <div className={`rounded-xl border-2 bg-white ${prominent ? "border-[#0B192C] shadow-sm p-6" : (historical ? "border-[#E5E5E0] p-4" : "border-[#E5E5E0] p-5")}`}
      data-testid={`coverage-card-${c.id}`}>
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          {prominent && <ShieldCheck className="w-5 h-5 text-[#0B192C]" />}
          <h3 className={`font-bold text-[#0B192C] ${prominent ? "text-lg" : "text-sm"}`}>
            {prominent ? "Current Medicare coverage" : (historical ? "Prior coverage" : "Coverage")}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-bold px-3 py-1 rounded-full ${STATE_PILL[c.lifecycle_state] || STATE_PILL.prior}`} data-testid={`coverage-state-${c.id}`}>{STATE_TXT[c.lifecycle_state] || c.lifecycle_state}</span>
          {c.verified
            ? <span className="text-xs font-bold px-3 py-1 rounded-full bg-[#DCFCE7] text-[#166534]" data-testid={`coverage-verified-${c.id}`} title="The reported plan was matched to a CMS plan identity. This is not an independent verification of the member's enrollment.">CMS plan matched</span>
            : <span className="text-xs font-bold px-3 py-1 rounded-full bg-[#FEF3C7] text-[#92400E]" data-testid={`coverage-unverified-${c.id}`}>No CMS match</span>}
          {av?.present && (
            <span className="text-xs font-bold px-3 py-1 rounded-full bg-[#EDE9FE] text-[#5B21B6]" data-testid={`coverage-advisor-verified-${c.id}`}
              title="An authorized advisor manually attested to an external BEQ (e.g. Sunfire) lookup result as of the checked date. This is advisor-reported external verification, not a live Nauticus query.">
              {av.label}
            </span>
          )}
          {book?.present && (
            <span className="text-xs font-bold px-3 py-1 rounded-full bg-[#E0F2FE] text-[#075985]" data-testid={`coverage-book-${c.id}`}
              title="An authorized agent attested this is business they personally wrote. Writing producer is distinct from the servicing advisor and the uploader.">
              {book.book_class === "new_production" ? "My book · new" : "My book · existing"}
            </span>
          )}
        </div>
      </div>

      {/* Verified identity is the source of truth for plan presentation */}
      {c.verified ? (
        <div className="rounded-lg bg-[#F0F7F4] border border-[#CDE7D8] p-4" data-testid={`coverage-verified-block-${c.id}`}>
          <p className="font-bold text-[#0B192C] text-base">{v.plan_name || "—"}</p>
          <p className="text-sm text-[#374151]">{v.carrier}</p>
          <p className="text-sm font-mono text-[#1E3E62] mt-1">{v.display_id} · {v.plan_year}</p>
          <div className="grid sm:grid-cols-3 gap-3 mt-3">
            <Info label="Status" value={LABEL(c.status)} />
            <Info label="Effective date" value={c.dates?.effective_date || "Not on file"} />
            <Info label="Writing agent" value={c.writing_agent_name} />
          </div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4" data-testid={`coverage-descriptive-only-${c.id}`}>
          <Info label="Carrier / org" value={desc.carrier_org} />
          <Info label="Plan" value={desc.plan_name || (desc.reported_text ? `"${desc.reported_text}" (client-reported, exact plan not confirmed)` : null)} />
          <Info label="Type" value={desc.plan_type} />
          <Info label="Status" value={LABEL(c.status)} />
          <Info label="Effective date" value={c.dates?.effective_date || "Not on file"} />
          <Info label="Writing agent" value={c.writing_agent_name} />
        </div>
      )}

      {/* Descriptive text kept but clearly secondary/historical when a verified identity exists */}
      {c.verified && (desc.carrier_org || desc.plan_name) && (
        <div className="mt-3" data-testid={`coverage-descriptive-${c.id}`}>
          <p className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">As recorded by advisor (descriptive)</p>
          {conflicts.length > 0 ? (
            <div className="rounded-lg border border-[#FDE68A] bg-[#FFFBEB] p-3" data-testid={`coverage-conflict-${c.id}`}>
              <p className="text-xs text-[#92400E]">
                Recorded as <b>{desc.carrier_org}{desc.plan_name ? ` · ${desc.plan_name}` : ""}</b>, which differs from the verified plan above. The verified CMS identity is authoritative; the descriptive text is kept for history.
              </p>
            </div>
          ) : (
            <p className="text-xs text-[#6B7280]">{desc.carrier_org}{desc.plan_name ? ` · ${desc.plan_name}` : ""}</p>
          )}
        </div>
      )}

      {/* Verified provenance — visually secondary */}
      {c.verified && (
        <p className="text-[11px] text-[#6B7280] mt-3" data-testid={`coverage-provenance-${c.id}`}>
          Verified identity · Source: <b>{LABEL(v.identity_source)}</b> · Validated {(v.validated_at || "").slice(0, 10)}{v.confirmed_by ? ` · by ${v.confirmed_by}` : ""}{v.conflict_confirmed ? " · conflict confirmed" : ""}
        </p>
      )}

      {/* Advisor-attested external (Sunfire) verification — distinct evidence tier */}
      {av?.present && (
        <div className="rounded-lg bg-[#F5F3FF] border border-[#DDD6FE] p-4 mt-3" data-testid={`coverage-advisor-block-${c.id}`}>
          <p className="text-sm font-bold text-[#5B21B6]">{av.label}</p>
          <p className="text-[11px] text-[#6B7280] mt-1">
            Advisor-reported external lookup (manual attestation) — not a live Nauticus query.
            {av.checked_date ? ` Checked ${av.checked_date}.` : ""}
            {av.source_as_of_date ? ` Source as of ${(av.source_as_of_date || "").slice(0, 10)}.` : ""}
            {av.attested_by ? ` By ${av.attested_by}.` : ""}
          </p>
          {(av.verified_facts || []).length > 0 && (
            <p className="text-xs text-[#374151] mt-2" data-testid={`coverage-advisor-facts-${c.id}`}>
              Verified facts: {av.verified_facts.map((f) => LABEL(f)).join(", ")}
            </p>
          )}
          {(av.returned_identity || av.returned_effective_date || av.returned_end_date) && (
            <p className="text-xs text-[#374151] mt-1">
              {av.returned_identity ? `Returned plan: ${typeof av.returned_identity === "string" ? av.returned_identity : (av.returned_identity.plan_name || av.returned_identity.display_id || "—")}. ` : ""}
              {av.returned_effective_date ? `Effective ${(av.returned_effective_date || "").slice(0, 10)}. ` : ""}
              {av.returned_end_date ? `Ends ${(av.returned_end_date || "").slice(0, 10)}.` : ""}
            </p>
          )}
          {av.conflict_with_prior && (
            <p className="text-xs font-semibold text-[#92400E] mt-2" data-testid={`coverage-advisor-conflict-${c.id}`}>
              ⚠ Conflicts with a prior verification — both are preserved in history ({av.history_count} prior).
            </p>
          )}
        </div>
      )}

      {/* Book attestation — writing-agent "business I wrote" */}
      {book?.present && (
        <div className="rounded-lg bg-[#F0F9FF] border border-[#BAE6FD] p-4 mt-3" data-testid={`coverage-book-block-${c.id}`}>
          <p className="text-sm font-bold text-[#075985]">{book.book_class === "new_production" ? "New production" : "Existing book"} — written by {book.writing_producer_name || "—"}</p>
          <p className="text-[11px] text-[#6B7280] mt-1">
            Agent-attested business ownership · {book.production_state === "effective" ? "Effective/active" : "Submitted (not yet effective)"}.
            {book.writing_agency_name ? ` Agency: ${book.writing_agency_name}.` : ""}
            {book.written_date ? ` Written ${(book.written_date || "").slice(0, 10)}.` : ""}
            {book.attested_by ? ` Attested by ${book.attested_by}` : ""}{book.attested_date ? ` on ${book.attested_date}.` : "."}
            {book.history_count > 0 ? ` (${book.history_count} prior in history)` : ""}
          </p>
          <p className="text-[11px] text-[#6B7280] mt-1">Writing producer is distinct from household assignment, servicing advisor, acquisition, and the document uploader.</p>
        </div>
      )}

      {!historical && (onAttach || onReplace || onEdit || onVerifyExternal || onBook) && (
        <div className="mt-4 pt-4 border-t border-dashed border-[#E5E5E0] flex flex-wrap gap-2">
          {onEdit && <Button size="sm" variant="outline" onClick={onEdit} data-testid={`coverage-edit-${c.id}`}><Pencil className="w-4 h-4 mr-1.5" />Edit record details</Button>}
          {onAttach && <Button size="sm" variant="outline" onClick={onAttach} data-testid={`coverage-attach-${c.id}`}><Search className="w-4 h-4 mr-1.5" />{c.verified ? "Change CMS plan" : "Find & attach CMS plan"}</Button>}
          {onReplace && <Button size="sm" className="bg-[#0B192C] text-white" onClick={onReplace} data-testid={`coverage-replace-${c.id}`}><Repeat className="w-4 h-4 mr-1.5" />Replace plan</Button>}
          {onVerifyExternal && <Button size="sm" variant="outline" className="border-[#C4B5FD] text-[#5B21B6]" onClick={onVerifyExternal} data-testid={`coverage-verify-external-${c.id}`}><ShieldCheck className="w-4 h-4 mr-1.5" />{av?.present ? "Update external verification" : "Record external verification"}</Button>}
          {onBook && <Button size="sm" variant="outline" className="border-[#7DD3FC] text-[#075985]" onClick={onBook} data-testid={`coverage-book-btn-${c.id}`}><BookMarked className="w-4 h-4 mr-1.5" />{book?.present ? "Update book attestation" : "Existing client / business I wrote"}</Button>}
        </div>
      )}
    </div>
  );
}

function BookAttestationDialog({ open, onOpenChange, hid, cov, onSaved }) {
  const existing = cov.book_attestation;
  const [bookClass, setBookClass] = useState(existing?.book_class || "existing_book");
  const [writtenDate, setWrittenDate] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async () => {
    setBusy(true); setErr("");
    try {
      await api.post(`/agent/households/${hid}/coverage/${cov.id}/book-attestation`, {
        book_class: bookClass, written_date: writtenDate || null, note: note || null });
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Could not record attestation."); setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="book-attestation-dialog">
        <DialogHeader><DialogTitle>Business I wrote — book attestation</DialogTitle></DialogHeader>
        <p className="text-xs text-[#6B7280]">You are attesting that this policy is business you personally wrote. This records writing-producer ownership (your canonical NPN) and is kept separate from household assignment, the servicing advisor, acquisition, and whoever uploaded documents.</p>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-xs">Classification</Label>
            <select value={bookClass} onChange={(e) => setBookClass(e.target.value)} className="h-10 w-full rounded-md border bg-white px-2 text-sm mt-1" data-testid="book-class-select">
              <option value="existing_book">Existing client (existing book — not a new sale this period)</option>
              <option value="new_production">New production (written this period)</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">Date written (optional)</Label>
            <Input type="date" value={writtenDate} onChange={(e) => setWrittenDate(e.target.value)} className="h-9 mt-1" data-testid="book-written-date" />
          </div>
          <div>
            <Label className="text-xs">Note (optional — no SSN/MBI/Medicaid #)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="mt-1" data-testid="book-note" />
          </div>
          {err && <p className="text-rose-600 text-sm" data-testid="book-error">{String(err)}</p>}
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button className="bg-[#075985] text-white hover:bg-[#0C4A6E]" onClick={submit} disabled={busy} data-testid="book-submit">{busy ? "Recording…" : "Record attestation"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReplaceCoverageDialog({ open, onOpenChange, hid, prior, onSaved }) {
  const [f, setF] = useState({ carrier_org: "", plan_name: "", plan_type: "", plan_year: 2026,
    effective_date: "", status: "active", writing_agent_name: "", prior_termination_date: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await api.post(`/agent/households/${hid}/coverage/${prior.id}/replace`, {
        ...f, plan_year: f.plan_year ? Number(f.plan_year) : null,
        plan_type: f.plan_type || null, effective_date: f.effective_date || null,
        prior_termination_date: f.prior_termination_date || null, reason: f.reason || null });
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || "Replace failed."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="coverage-replace-dialog">
        <DialogHeader><DialogTitle>Replace current plan</DialogTitle></DialogHeader>
        <p className="text-sm text-[#6B7280]">Records a genuine plan change. The prior plan is preserved in history — it's end-dated only if you enter a termination date. After saving, attach the new plan's verified CMS identity on the new record.</p>
        <div className="grid grid-cols-2 gap-2 mt-1">
          <div className="col-span-2"><Label className="text-xs">New carrier / org</Label><Input value={f.carrier_org} onChange={(e) => setF({ ...f, carrier_org: e.target.value })} className="h-9 mt-1" data-testid="replace-carrier" /></div>
          <div className="col-span-2"><Label className="text-xs">New plan name</Label><Input value={f.plan_name} onChange={(e) => setF({ ...f, plan_name: e.target.value })} className="h-9 mt-1" data-testid="replace-planname" /></div>
          <div><Label className="text-xs">Plan year</Label><Input type="number" value={f.plan_year} onChange={(e) => setF({ ...f, plan_year: e.target.value })} className="h-9 mt-1" data-testid="replace-year" /></div>
          <div><Label className="text-xs">Status</Label>
            <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className="h-9 mt-1 w-full rounded-md border border-[#E5E5E0] px-2 text-sm" data-testid="replace-status">
              <option value="active">Active</option><option value="submitted">Submitted</option><option value="pending">Pending</option>
            </select>
          </div>
          <div><Label className="text-xs">New effective date (if known)</Label><Input type="date" value={f.effective_date} onChange={(e) => setF({ ...f, effective_date: e.target.value })} className="h-9 mt-1" data-testid="replace-effective" /></div>
          <div><Label className="text-xs">Prior plan end date (if known)</Label><Input type="date" value={f.prior_termination_date} onChange={(e) => setF({ ...f, prior_termination_date: e.target.value })} className="h-9 mt-1" data-testid="replace-prior-term" /></div>
          <div className="col-span-2"><Label className="text-xs">Reason (optional)</Label><Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} className="h-9 mt-1" data-testid="replace-reason" /></div>
        </div>
        {err && <p className="text-rose-600 text-sm" data-testid="replace-error">{String(err)}</p>}
        <div className="flex justify-end gap-2 mt-1">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button className="bg-[#0B192C] text-white" onClick={submit} disabled={busy} data-testid="replace-submit">{busy ? "Saving…" : "Replace & preserve prior"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EditCoverageDialog({ open, onOpenChange, hid, cov, onSaved }) {
  const desc = cov.descriptive || {};
  const [f, setF] = useState({ carrier_org: desc.carrier_org || "", plan_name: desc.plan_name || "",
    plan_type: desc.plan_type || "", status: cov.status || "submitted",
    effective_date: cov.dates?.effective_date || "", writing_agent_name: cov.writing_agent_name || "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await api.patch(`/agent/households/${hid}/coverage/${cov.id}`, {
        carrier_org: f.carrier_org || null, plan_name: f.plan_name || null,
        plan_type: f.plan_type || null, status: f.status,
        effective_date: f.effective_date || null, writing_agent_name: f.writing_agent_name || null });
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || "Update failed."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="coverage-edit-dialog">
        <DialogHeader><DialogTitle>Edit coverage record details</DialogTitle></DialogHeader>
        <p className="text-sm text-[#6B7280]">Edits descriptive/administrative fields only. The verified CMS plan identity is changed separately with “Change CMS plan.”</p>
        <div className="grid grid-cols-2 gap-2 mt-1">
          <div className="col-span-2"><Label className="text-xs">Carrier / org</Label><Input value={f.carrier_org} onChange={(e) => setF({ ...f, carrier_org: e.target.value })} className="h-9 mt-1" data-testid="edit-carrier" /></div>
          <div className="col-span-2"><Label className="text-xs">Plan name</Label><Input value={f.plan_name} onChange={(e) => setF({ ...f, plan_name: e.target.value })} className="h-9 mt-1" data-testid="edit-planname" /></div>
          <div><Label className="text-xs">Status</Label>
            <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className="h-9 mt-1 w-full rounded-md border border-[#E5E5E0] px-2 text-sm" data-testid="edit-status">
              <option value="submitted">Submitted</option><option value="pending">Pending</option><option value="active">Active</option>
              <option value="declined">Declined</option><option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div><Label className="text-xs">Effective date</Label><Input type="date" value={(f.effective_date || "").slice(0, 10)} onChange={(e) => setF({ ...f, effective_date: e.target.value })} className="h-9 mt-1" data-testid="edit-effective" /></div>
          <div className="col-span-2"><Label className="text-xs">Writing agent</Label><Input value={f.writing_agent_name} onChange={(e) => setF({ ...f, writing_agent_name: e.target.value })} className="h-9 mt-1" data-testid="edit-writing-agent" /></div>
        </div>
        {err && <p className="text-rose-600 text-sm" data-testid="edit-error">{String(err)}</p>}
        <div className="flex justify-end gap-2 mt-1">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button className="bg-[#0B192C] text-white" onClick={submit} disabled={busy} data-testid="edit-submit">{busy ? "Saving…" : "Save details"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const _VERIFIABLE_FACTS = [
  { value: "current_coverage_identity", label: "Current coverage identity" },
  { value: "carrier", label: "Carrier" },
  { value: "plan_name", label: "Plan name" },
  { value: "plan_type", label: "Plan type" },
  { value: "effective_date", label: "Effective date" },
  { value: "end_date", label: "End date" },
];

function VerifyExternalDialog({ open, onOpenChange, hid, cov, onSaved }) {
  const existing = cov?.advisor_verified;
  const [f, setF] = useState({
    source: "sunfire", source_label: "Sunfire",
    checked_at: new Date().toISOString().slice(0, 10),
    source_as_of_date: "", returned_plan_name: "",
    returned_effective_date: "", returned_end_date: "",
    confirmation_number: "", notes: "",
  });
  const [facts, setFacts] = useState(["current_coverage_identity"]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const toggleFact = (v) => setFacts((arr) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]));

  const submit = async () => {
    setErr(null);
    if (!f.source.trim()) { setErr("Source is required (e.g. Sunfire)."); return; }
    if (facts.length === 0) { setErr("Select at least one coverage fact you verified."); return; }
    setBusy(true);
    try {
      await api.post(`/agent/households/${hid}/coverage/${cov.id}/verify-external`, {
        source: f.source.trim(), source_label: f.source_label.trim() || f.source.trim(),
        verified_facts: facts,
        checked_at: f.checked_at || null,
        source_as_of_date: f.source_as_of_date || null,
        returned_identity: f.returned_plan_name ? { plan_name: f.returned_plan_name } : null,
        returned_effective_date: f.returned_effective_date || null,
        returned_end_date: f.returned_end_date || null,
        confirmation_number: f.confirmation_number || null,
        notes: f.notes || null,
      });
      onSaved();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === "object" ? (detail.message || "Could not record verification.") : (detail || "Could not record verification."));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="verify-external-dialog">
        <DialogHeader><DialogTitle>Record external coverage verification</DialogTitle></DialogHeader>
        <p className="text-sm text-[#6B7280]">
          Record an authorized external BEQ result you looked up (e.g. Sunfire), as a MANUAL attestation
          AS OF the date you checked. This is advisor-reported — not a live Nauticus query. Only coverage
          facts may be attested; never Medicaid level, eligibility, election period or provider participation.
          Do not enter SSN/MBI/Medicaid numbers anywhere here.
        </p>
        {existing?.present && (
          <p className="text-xs text-[#5B21B6] bg-[#F5F3FF] border border-[#DDD6FE] rounded-md p-2" data-testid="verify-external-existing">
            A prior verification exists ({existing.label}). Saving a new one preserves the prior in history.
          </p>
        )}
        <div className="grid grid-cols-2 gap-2 mt-1">
          <div><Label className="text-xs">Source (system)</Label><Input value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} className="h-9 mt-1" data-testid="verify-source" /></div>
          <div><Label className="text-xs">Source label</Label><Input value={f.source_label} onChange={(e) => setF({ ...f, source_label: e.target.value })} className="h-9 mt-1" data-testid="verify-source-label" /></div>
          <div><Label className="text-xs">Checked on</Label><Input type="date" value={f.checked_at} onChange={(e) => setF({ ...f, checked_at: e.target.value })} className="h-9 mt-1" data-testid="verify-checked-at" /></div>
          <div><Label className="text-xs">Source as-of date</Label><Input type="date" value={f.source_as_of_date} onChange={(e) => setF({ ...f, source_as_of_date: e.target.value })} className="h-9 mt-1" data-testid="verify-source-as-of" /></div>
        </div>
        <div className="mt-2">
          <Label className="text-xs">Which facts did you verify? *</Label>
          <div className="flex flex-wrap gap-2 mt-1.5" data-testid="verify-facts">
            {_VERIFIABLE_FACTS.map((ff) => {
              const on = facts.includes(ff.value);
              return (
                <button key={ff.value} type="button" onClick={() => toggleFact(ff.value)}
                  data-testid={`verify-fact-${ff.value}`}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-full border-2 transition-colors ${on ? "border-[#5B21B6] bg-[#EDE9FE] text-[#5B21B6]" : "border-[#E5E5E0] bg-white text-[#475569] hover:border-[#C4B5FD]"}`}>
                  {on && <Check className="w-3 h-3 mr-1 inline -mt-0.5" />}{ff.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 mt-1">
          <div className="col-span-2"><Label className="text-xs">Returned plan name (optional)</Label><Input value={f.returned_plan_name} onChange={(e) => setF({ ...f, returned_plan_name: e.target.value })} className="h-9 mt-1" data-testid="verify-returned-plan" /></div>
          <div><Label className="text-xs">Returned effective date</Label><Input type="date" value={f.returned_effective_date} onChange={(e) => setF({ ...f, returned_effective_date: e.target.value })} className="h-9 mt-1" data-testid="verify-returned-effective" /></div>
          <div><Label className="text-xs">Returned end date</Label><Input type="date" value={f.returned_end_date} onChange={(e) => setF({ ...f, returned_end_date: e.target.value })} className="h-9 mt-1" data-testid="verify-returned-end" /></div>
          <div className="col-span-2"><Label className="text-xs">Confirmation # (optional)</Label><Input value={f.confirmation_number} onChange={(e) => setF({ ...f, confirmation_number: e.target.value })} className="h-9 mt-1" data-testid="verify-confirmation" /></div>
          <div className="col-span-2"><Label className="text-xs">Notes (no sensitive identifiers)</Label><Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="mt-1" rows={2} data-testid="verify-notes" /></div>
        </div>
        {err && <p className="text-rose-600 text-sm" data-testid="verify-external-error">{String(err)}</p>}
        <div className="flex justify-end gap-2 mt-1">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button className="bg-[#5B21B6] text-white hover:bg-[#4C1D95]" onClick={submit} disabled={busy} data-testid="verify-external-submit">{busy ? "Recording…" : "Record verification"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function AttachCmsDialog({ open, onOpenChange, hid, coverageId, cov, onSaved }) {
  const [f, setF] = useState({ plan_year: 2026, org: "", plan_name: "", contract_id: "", pbp: "", segment_id: "", county: "", state: "" });
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [selected, setSelected] = useState(null);
  const [conflicts, setConflicts] = useState(null);

  const search = async () => {
    setBusy(true); setErr(null); setSelected(null); setConflicts(null);
    try {
      const { data } = await api.get("/agent/plan-search", { params: { ...f } });
      setRes(data);
    } catch (e) { setErr(e?.response?.data?.detail || "Search failed."); }
    finally { setBusy(false); }
  };

  const attach = async (plan, confirm_conflict = false) => {
    setBusy(true); setErr(null);
    try {
      const { data } = await api.post(`/agent/households/${hid}/coverage/${coverageId}/cms-identity`, {
        plan_year: plan.plan_year, contract_id: plan.contract_id, pbp: plan.pbp,
        segment_id: plan.segment_id, confirm_conflict });
      if (data.status === "needs_confirmation") { setSelected(plan); setConflicts(data.conflicts); }
      else { onSaved(); }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === "object" ? (detail.message || "Save rejected.") : (detail || "Save failed."));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="cms-attach-dialog">
        <DialogHeader><DialogTitle>Find & attach exact CMS plan</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <div><Label className="text-xs">Plan year</Label><Input type="number" value={f.plan_year} onChange={(e) => setF({ ...f, plan_year: e.target.value })} className="h-9 mt-1" data-testid="cms-search-year" /></div>
          <div className="col-span-2"><Label className="text-xs">Carrier / organization</Label><Input value={f.org} onChange={(e) => setF({ ...f, org: e.target.value })} placeholder="e.g. Devoted" className="h-9 mt-1" data-testid="cms-search-org" /></div>
          <div><Label className="text-xs">Plan name</Label><Input value={f.plan_name} onChange={(e) => setF({ ...f, plan_name: e.target.value })} className="h-9 mt-1" data-testid="cms-search-planname" /></div>
          <div><Label className="text-xs">Contract</Label><Input value={f.contract_id} onChange={(e) => setF({ ...f, contract_id: e.target.value })} placeholder="H1290" className="h-9 mt-1" data-testid="cms-search-contract" /></div>
          <div><Label className="text-xs">PBP</Label><Input value={f.pbp} onChange={(e) => setF({ ...f, pbp: e.target.value })} placeholder="013" className="h-9 mt-1" data-testid="cms-search-pbp" /></div>
          <div><Label className="text-xs">Segment</Label><Input value={f.segment_id} onChange={(e) => setF({ ...f, segment_id: e.target.value })} placeholder="000" className="h-9 mt-1" data-testid="cms-search-segment" /></div>
          <div><Label className="text-xs">County</Label><Input value={f.county} onChange={(e) => setF({ ...f, county: e.target.value })} className="h-9 mt-1" data-testid="cms-search-county" /></div>
        </div>
        <Button onClick={search} disabled={busy} className="bg-[#0B192C] text-white w-full sm:w-auto self-start" data-testid="cms-search-btn">
          <Search className="w-4 h-4 mr-1.5" />{busy ? "Searching…" : "Search Plan Intelligence"}
        </Button>
        {err && <p className="text-rose-600 text-sm" data-testid="cms-attach-error">{err}</p>}

        {conflicts && (
          <div className="rounded-lg border-2 border-[#FDE68A] bg-[#FFFBEB] p-3" data-testid="cms-conflict-warning">
            <p className="text-sm font-bold text-[#92400E] mb-1">Descriptive information conflicts with this plan</p>
            <ul className="text-xs text-[#92400E] list-disc pl-4">
              {conflicts.map((c, i) => <li key={i}>{LABEL(c.field)}: recorded “{c.recorded}” vs CMS “{c.cms_organization || c.cms_plan_name}”</li>)}
            </ul>
            <Button size="sm" className="mt-2 bg-[#92400E] text-white" onClick={() => attach(selected, true)} disabled={busy} data-testid="cms-confirm-conflict-btn">Attach anyway (confirm)</Button>
          </div>
        )}

        {res && (
          <div data-testid="cms-search-results">
            <p className="text-xs text-[#6B7280] mb-2">{res.returned} of {res.total} matching plans (select the exact plan — no auto-match)</p>
            <div className="space-y-2 max-h-[40vh] overflow-y-auto">
              {res.results.map((p) => (
                <div key={p.display_id} className="rounded-lg border border-[#E5E5E0] p-3 flex items-center justify-between gap-3" data-testid={`cms-result-${p.display_id}`}>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm text-[#0B192C] truncate">{p.plan_name}</p>
                    <p className="text-xs text-[#6B7280] truncate">{p.organization_marketing_name} · {p.plan_type_label || p.hpms_plan_type}{p.is_snp ? " · SNP" : ""}</p>
                    <p className="text-xs font-mono text-[#1E3E62]">{p.display_id} · {p.plan_year}</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => attach(p)} disabled={busy} data-testid={`cms-select-${p.display_id}`}>Select</Button>
                </div>
              ))}
              {res.results.length === 0 && <p className="text-sm text-[#6B7280]">No plans matched. Refine your search.</p>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function HouseholdTexts({ hid }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get(`/sms/household/${hid}`).then((r) => setRows(r.data.messages || [])).catch(() => setRows([])); }, [hid]);
  if (!rows || rows.length === 0) return null;
  return (
    <div className="mt-4 rounded-xl border border-[#E5E2DA] bg-white p-3" data-testid="household-texts">
      <p className="text-xs font-semibold uppercase tracking-wide text-[#1E3E62] mb-2">Text history</p>
      {rows.map((m) => (
        <div key={m.id} className="text-xs text-[#1E3E62] py-1 border-t border-[#F0EEE8]" data-testid={`household-text-${m.id}`}>
          <span className="font-semibold">{m.direction === "inbound" ? "From client" : "To client"}</span> · {m.body} <span className="text-[#8A8F98]">· {m.status} · {(m.created_at || "").slice(0, 16).replace("T", " ")}</span>
        </div>
      ))}
    </div>
  );
}

const REASON_TEXT = {
  not_listed_in_queried_directory: "Not found at this location in the plan's 2027 directory we checked",
  scan_in_progress: "Directory check still in progress",
  carrier_unreachable: "The carrier's 2027 drug list couldn't be reached",
  no_formulary_endpoint: "No 2027 drug list available from this carrier yet",
  pdf_import_incomplete: "The plan's 2027 drug list isn't fully loaded yet",
  plan_not_in_formulary_feed: "This plan's drug list wasn't found in the carrier's formulary feed",
  source_not_connected: "This plan's directory or drug list isn't connected yet",
};

const plainReason = (p) => {
  if (!p.detail && !p.reason) return "";
  const first = p.reason ? (p.reason.match(/^.*?\.(\s|$)/) || [p.reason])[0].trim() : null;
  const t = first || REASON_TEXT[p.detail] || (p.detail && !/^[a-z0-9_]+$/.test(p.detail) ? p.detail : null) || "Not yet verified";
  return ` — ${t}`;
};

function SavedDoctors({ rows }) {
  return (
    <div data-testid="profile-saved-doctors">
      <p className="text-xs text-[#6B7280] mb-1.5">Doctors saved by the consumer (consumer-reported; directory listing only, not a coverage decision)</p>
      <ul className="space-y-2">
        {rows.map((r) => (
          <li key={`${r.practitioner_id}-${r.location_id}`} className="text-sm rounded-lg border border-[#E5E2DA] p-2.5" data-testid={`profile-saved-doctor-${r.location_id.slice(0, 8)}`}>
            <div className="font-semibold text-[#0B192C]">{r.name}{r.npi ? ` · NPI ${r.npi}` : ""}{r.role ? ` · ${r.role === "pcp" ? "Primary care" : "Specialist"}` : ""}{r.manual ? " · entered manually" : ""}</div>
            <div className="text-[#6B7280]">{r.address}</div>
            {r.unresolved_checks > 0 && <div className="text-xs text-[#B45309]" data-testid={`profile-saved-doctor-unresolved-${r.location_id.slice(0, 8)}`}>{r.unresolved_checks} plan check(s) need verification</div>}
            {(r.participation || []).map((p) => {
              const stale = p.checked_at && Date.now() - new Date(p.checked_at).getTime() > 7 * 86400000;
              return <div key={p.plan} className="text-xs text-[#1E3E62]">{p.plan}: {p.label}{p.network ? ` (${p.network})` : ""}{plainReason(p)}{p.checked_at ? ` · checked ${p.checked_at.slice(0, 10)}` : ""}{stale ? <b className="text-[#B45309]" data-testid="profile-saved-doctor-stale"> · EXPIRED — recheck</b> : null}</div>;
            })}
          </li>
        ))}
      </ul>
    </div>
  );
}

function SavedMedications({ rows }) {
  return (
    <div data-testid="profile-saved-medications">
      <p className="text-xs text-[#6B7280] mb-1.5">Medications saved by the consumer (formulary listing per plan; no cost estimate)</p>
      <ul className="space-y-2">
        {rows.map((r) => (
          <li key={r.med_key} className="text-sm rounded-lg border border-[#E5E2DA] p-2.5" data-testid={`profile-saved-med-${r.med_key}`}>
            <div className="font-semibold text-[#0B192C]">{r.name} · RxCUI {r.rxcui}</div>
            <div className="text-[#6B7280]">Qty {r.quantity} every {r.refill_days} days · {r.pharmacy?.kind === "mail" ? "Mail order" : (r.pharmacy?.name || "Retail")}{r.pharmacy?.zip ? ` (${r.pharmacy.zip})` : ""}</div>
            {r.unresolved_checks > 0 && <div className="text-xs text-[#B45309]" data-testid={`profile-saved-med-unresolved-${r.med_key}`}>{r.unresolved_checks} plan check(s) need verification</div>}
            {r.coverage_status === "recheck_required" && <div className="text-xs text-[#B45309]" data-testid={`profile-saved-med-recheck-${r.med_key}`}>Earlier plan checks were withdrawn. Needs verification — reopen the comparison to recheck.</div>}
            {(r.coverage || []).map((p) => {
              const stale = p.checked_at && Date.now() - new Date(p.checked_at).getTime() > 7 * 86400000;
              const f = [p.prior_auth && "PA", p.step_therapy && "ST", p.quantity_limit && (p.quantity_limit_text ? `QL ${p.quantity_limit_text}` : "QL")].filter(Boolean);
              return <div key={p.plan} className="text-xs text-[#1E3E62]">{p.plan}: {p.label}{f.length ? ` (${f.join(", ")})` : ""}{plainReason(p)}{p.checked_at ? ` · checked ${p.checked_at.slice(0, 10)}` : ""}{stale ? <b className="text-[#B45309]"> · EXPIRED — recheck</b> : null}</div>;
            })}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Needs({ d, hid, reload }) {
  const cands = d.journey?.candidate_plans || [];
  const plans = cands.filter((c) => c.contract_id).map((c) => ({ contract_id: c.contract_id, pbp: c.pbp, segment_id: c.segment_id || "0" }));
  const needs = d.needs || [];
  const intel = d.assessment?.intelligence || {};
  const derived = d.assessment?.derived_needs || [];
  return (
    <Card>
      <h3 className="font-bold text-[#0B192C] mb-3">Household needs</h3>
      {needs.length === 0 && derived.length === 0 && (intel.segments || []).length === 0 ? (
        <p className="text-sm text-[#6B7280]" data-testid="profile-needs-empty">No needs captured yet. They populate from the completed assessment.</p>
      ) : (
        <div className="space-y-4" data-testid="profile-needs">
          {needs.length > 0 && <TagRow title="Stated needs" tags={needs} />}
          {derived.length > 0 && <TagRow title="Derived needs (rules_v1)" tags={derived} />}
          {(intel.segments || []).length > 0 && <TagRow title="Segments (rules_v1)" tags={intel.segments} />}
          {intel.next_best_action && <Info label="Next best action" value={LABEL(intel.next_best_action)} />}
        </div>
      )}
      <CandidatePlansEditor hid={hid} cands={cands} onChanged={reload} />
      <MyDoctors plans={plans} zip={d.person?.zip_code || ""} baseOverride={`/agent/households/${hid}/my-doctors`} />
      <MyMedications plans={plans} baseOverride={`/agent/households/${hid}/my-meds`} />
    </Card>
  );
}
function TagRow({ title, tags }) {
  return (
    <div>
      <p className="text-xs text-[#6B7280] mb-1.5">{title}</p>
      <div className="flex flex-wrap gap-2">{tags.map((t, i) => <span key={i} className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#EEF2F6] text-[#1E3E62]">{LABEL(String(t))}</span>)}</div>
    </div>
  );
}

function Activity({ hid }) {
  const [items, setItems] = useState(null);
  const [note, setNote] = useState("");
  const [shared, setShared] = useState(false);
  const [due, setDue] = useState("");
  const [state, setState] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = async () => { const { data } = await api.get(`/agent/households/${hid}/activities`); setItems(data.activities); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [hid]);
  const addNote = async () => {
    if (!note.trim()) return;
    setState("saving");
    try {
      await api.post(`/agent/households/${hid}/notes`, { body: note, visibility: shared ? "shared" : "internal" });
      if (due) await api.post(`/agent/households/${hid}/tasks`, { title: `Follow up: ${note.trim().slice(0, 60)}`, due_date: due, priority: "medium" });
      setNote(""); setShared(false); setDue(""); setState("saved"); await load();
    } catch { setState("error"); }
  };
  const saveEdit = async () => {
    setState("saving");
    try { await api.patch(`/agent/notes/${editing.id}`, { body: editing.body, visibility: editing.visibility, expected_body: editing.orig }); setEditing(null); setState("saved"); await load(); }
    catch (e) { setState("error"); toast.error(e?.response?.data?.detail || "Couldn't save the note."); }
  };
  return (
    <Card>
      <div className="mb-5 space-y-2">
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" rows={2} className="bg-white" data-testid="activity-note-input" />
        <div className="flex flex-wrap items-center gap-3 text-xs">
          <label className="flex items-center gap-1"><input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} data-testid="activity-note-shared" />Share with consumer (otherwise internal)</label>
          <label className="flex items-center gap-1">Follow-up due <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="h-8 w-36" data-testid="activity-note-followup" /></label>
          <Button onClick={addNote} disabled={state === "saving" || !note.trim()} className="bg-[#0B192C] text-white h-8" data-testid="activity-add-note"><Plus className="w-4 h-4 mr-1" />Note</Button>
          <SaveState state={state} onRetry={editing ? saveEdit : addNote} testid="activity-save-state" />
        </div>
      </div>
      {!items ? <Skeleton className="h-40 w-full" /> : items.length === 0 ? (
        <p className="text-sm text-[#6B7280]" data-testid="activity-empty">No activity yet.</p>
      ) : (
        <ol className="relative border-l-2 border-[#EEE] pl-5 space-y-4" data-testid="activity-timeline">
          {items.map((a) => {
            const Icon = ACT_ICON[a.type] || Clock;
            return (
              <li key={a.id} className="relative" data-testid={`activity-item-${a.type}`}>
                <span className="absolute -left-[27px] top-0.5 w-5 h-5 rounded-full bg-white border-2 border-[#0B192C] grid place-items-center"><Icon className="w-2.5 h-2.5 text-[#0B192C]" /></span>
                <p className="text-sm font-semibold text-[#0B192C]">{a.title}{a.type === "note" && <span className={`ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${a.metadata?.visibility === "shared" ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#EEF2F6] text-[#1E3E62]"}`} data-testid={`note-visibility-${a.id}`}>{a.metadata?.visibility === "shared" ? "Shared with consumer" : "Internal"}</span>}</p>
                {editing?.id === a.id ? (
                  <div className="space-y-1">
                    <Textarea value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} rows={2} data-testid={`note-edit-input-${a.id}`} />
                    <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={editing.visibility === "shared"} onChange={(e) => setEditing({ ...editing, visibility: e.target.checked ? "shared" : "internal" })} />Share with consumer</label>
                    <div className="flex gap-2"><Button size="sm" onClick={saveEdit} data-testid={`note-edit-save-${a.id}`}>Save</Button><Button size="sm" variant="ghost" onClick={() => setEditing(null)}>Cancel</Button></div>
                  </div>
                ) : a.body && <p className="text-sm text-[#374151]">{a.body}</p>}
                <p className="text-xs text-[#9CA3AF]">{(a.occurred_at || "").slice(0, 16).replace("T", " ")} · {LABEL(a.type)}{a.metadata?.author_name ? ` · ${a.metadata.author_name}` : ""}{a.metadata?.edited_at ? ` · edited ${a.metadata.edited_at.slice(0, 16).replace("T", " ")} (${(a.metadata.revisions || []).length} earlier version${(a.metadata.revisions || []).length === 1 ? "" : "s"} kept)` : ""}
                  {a.type === "note" && editing?.id !== a.id && <button type="button" className="ml-2 underline text-[#1E3E62]" onClick={() => setEditing({ id: a.id, body: a.body || "", orig: a.body || "", visibility: a.metadata?.visibility || "internal" })} data-testid={`note-edit-${a.id}`}>Edit</button>}</p>
              </li>
            );
          })}
        </ol>
      )}
    </Card>
  );
}

function TasksTab({ hid }) {
  const [rows, setRows] = useState(null);
  const [f, setF] = useState({ priority: "medium" });
  const [busy, setBusy] = useState(false);
  const load = async () => { const { data } = await api.get(`/agent/households/${hid}/tasks`); setRows(data.tasks); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [hid]);
  const create = async () => {
    if (!f.title?.trim()) return;
    setBusy(true);
    try { await api.post(`/agent/households/${hid}/tasks`, { title: f.title, description: f.description || null, due_date: f.due_date || null, priority: f.priority }); setF({ priority: "medium" }); await load(); }
    finally { setBusy(false); }
  };
  const complete = async (id) => { await api.post(`/agent/tasks/${id}/complete`); await load(); };
  const snooze = async (id) => { const t = new Date(Date.now() + 3 * 864e5).toISOString().slice(0, 10); await api.post(`/agent/tasks/${id}/snooze`, { snooze_until: t }); await load(); };
  return (
    <Card>
      <div className="grid sm:grid-cols-[1fr_140px_150px_auto] gap-2 items-end mb-5">
        <div><Label className="text-xs">New task</Label><Input value={f.title || ""} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Call to confirm effective date" className="bg-white h-10 mt-1" data-testid="task-title-input" /></div>
        <div><Label className="text-xs">Due date</Label><Input type="date" value={f.due_date || ""} onChange={(e) => setF({ ...f, due_date: e.target.value })} className="bg-white h-10 mt-1" data-testid="task-due-input" /></div>
        <div><Label className="text-xs">Priority</Label>
          <select value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} className="h-10 mt-1 w-full rounded-md border bg-white px-2" data-testid="task-priority-input">
            {["low", "medium", "high"].map((p) => <option key={p} value={p}>{LABEL(p)}</option>)}
          </select></div>
        <Button onClick={create} disabled={busy || !f.title?.trim()} className="h-10 bg-[#0B192C] text-white" data-testid="task-create-btn"><Plus className="w-4 h-4 mr-1" />Add</Button>
      </div>
      {!rows ? <Skeleton className="h-32 w-full" /> : rows.length === 0 ? (
        <p className="text-sm text-[#6B7280]" data-testid="tasks-empty">No tasks yet.</p>
      ) : (
        <ul className="space-y-2" data-testid="tasks-list">
          {rows.map((t) => (
            <li key={t.id} className={`flex items-center gap-3 p-3 rounded-lg border ${t.status === "done" ? "bg-[#F8F8F5] opacity-70" : "bg-white"}`} data-testid={`task-${t.id}`}>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${PRIO[t.priority] || PRIO.medium}`}>{LABEL(t.priority)}</span>
              <div className="flex-1">
                <p className={`text-sm font-semibold text-[#0B192C] ${t.status === "done" ? "line-through" : ""}`}>{t.title}</p>
                <p className="text-xs text-[#9CA3AF]">{t.due_date ? `Due ${t.due_date}` : "No due date"} · {LABEL(t.status)}</p>
              </div>
              {t.status !== "done" && <>
                <Button size="sm" variant="ghost" onClick={() => snooze(t.id)} data-testid={`task-snooze-${t.id}`}><AlarmClock className="w-4 h-4" /></Button>
                <Button size="sm" variant="outline" onClick={() => complete(t.id)} data-testid={`task-complete-${t.id}`}><Check className="w-4 h-4 mr-1" />Done</Button>
              </>}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function Documents({ hid }) {
  return (
    <div className="space-y-6">
      <SunfireEnrollments hid={hid} />
      <HouseholdDocuments hid={hid} />
    </div>
  );
}

const EVIDENCE_PILL = {
  Confirmed: "bg-[#DCFCE7] text-[#166534]",
  "Consumer-reported": "bg-[#FEF3C7] text-[#92400E]",
  Unresolved: "bg-[#FEE2E2] text-[#991B1B]",
};

const OUTCOME_OPTIONS = [
  ["reached", "Reached — spoke with household"], ["left_voicemail", "Left voicemail"],
  ["no_answer", "No answer"], ["scheduled", "Scheduled a follow-up"],
  ["enrolled", "Enrolled / application started"], ["not_interested", "Not interested"],
  ["not_eligible", "Not eligible right now"],
];

function HandoffOutcome({ hid, handoff, reload }) {
  const [editing, setEditing] = useState(false);
  const [outcome, setOutcome] = useState("reached");
  const [note, setNote] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [busy, setBusy] = useState(false);
  const recorded = !!handoff.first_contact_at;
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/agent/households/${hid}/handoff-outcome`,
        { outcome, note: note || null, follow_up_date: followUp || null });
      setEditing(false); setNote(""); setFollowUp("");
      await reload();
    } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-[#E5E5E0] bg-[#FBFBF8] p-3" data-testid="handoff-outcome">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-bold text-[#0B192C]">First-contact outcome</p>
        {!editing && (
          <Button size="sm" variant={recorded ? "ghost" : "outline"} onClick={() => setEditing(true)} data-testid="handoff-outcome-open">
            {recorded ? <><Pencil className="w-3.5 h-3.5 mr-1" />Update</> : "Record outcome"}
          </Button>
        )}
      </div>
      {recorded && !editing && (
        <p className="text-sm text-[#374151] mt-1" data-testid="handoff-outcome-recorded">
          <span className="font-semibold">{handoff.outcome_label || LABEL(handoff.outcome)}</span>
          {handoff.outcome_at ? <span className="text-[#9CA3AF]"> · {(handoff.outcome_at || "").slice(0, 16).replace("T", " ")}</span> : null}
          {handoff.outcome_note ? <span className="block text-[#6B7280]">{handoff.outcome_note}</span> : null}
        </p>
      )}
      {!recorded && !editing && (
        <p className="text-xs text-[#92400E] mt-1" data-testid="handoff-outcome-pending">No outcome recorded yet — this request is still awaiting first contact.</p>
      )}
      {editing && (
        <div className="space-y-2 mt-2">
          <select value={outcome} onChange={(e) => setOutcome(e.target.value)} className="h-10 w-full rounded-md border bg-white px-2" data-testid="handoff-outcome-select">
            {OUTCOME_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Note (optional)" className="bg-white" data-testid="handoff-outcome-note" />
          <div className="flex items-center gap-2">
            <Label className="text-xs shrink-0">Follow-up</Label>
            <Input type="date" value={followUp} onChange={(e) => setFollowUp(e.target.value)} className="h-9 bg-white" data-testid="handoff-outcome-followup" />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={save} disabled={busy} className="bg-[#0B192C] text-white" data-testid="handoff-outcome-save">Save</Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function JourneyContext({ ctx, hid, reload }) {
  if (ctx === null) return <Card><Skeleton className="h-64 w-full" /></Card>;
  const facts = ctx.facts || [];
  const plans = ctx.plans_in_review || [];
  const ho = ctx.handoff;
  const na = ctx.next_action;

  return (
    <div className="space-y-4" data-testid="journey-context">
      {/* Objective + next action */}
      <Card>
        <div className="flex items-center gap-2 mb-2"><Navigation className="w-4 h-4 text-[#0B192C]" /><h3 className="font-bold text-[#0B192C]">Current objective</h3></div>
        <p className="text-sm text-[#0B192C]" data-testid="journey-objective">{ctx.objective || "No stated objective yet."}</p>
        {na && (
          <div className="mt-3 rounded-lg bg-[#F3F8FF] border border-[#CFE0F5] p-3 flex items-start gap-2" data-testid="journey-next-action">
            <ArrowRight className="w-4 h-4 text-[#1E3E62] mt-0.5 shrink-0" />
            <p className="text-sm text-[#0B192C]"><span className="font-bold">Next action:</span> {na.label}{na.detail ? <span className="text-[#6B7280]"> — {na.detail}</span> : null}</p>
          </div>
        )}
      </Card>

      {/* Handoff */}
      <Card>
        <div className="flex items-center gap-2 mb-3"><Flag className="w-4 h-4 text-[#0B192C]" /><h3 className="font-bold text-[#0B192C]">Advisor handoff</h3></div>
        {!ho ? (
          <p className="text-sm text-[#6B7280]" data-testid="journey-handoff-none">No advisor handoff has been requested for this household.</p>
        ) : (
          <div className="space-y-3" data-testid="journey-handoff">
            <Info label="Reason" value={ho.reason} />
            <div className="grid sm:grid-cols-3 gap-3">
              <Info label="Requested" value={(ho.created_at || "").slice(0, 16).replace("T", " ")} />
              <Info label="Status" value={ho.outcome_label || LABEL(ho.status)} />
              <Info label="Contact consent" value={ho.consent ? "Given" : "Not given"} />
            </div>
            {ho.contact && (ho.contact.phone || ho.contact.email) && (
              <div className="rounded-lg border border-[#CDE7D8] bg-[#F0F7F4] p-3 text-sm" data-testid="journey-permitted-contact">
                <p className="font-bold text-[#1F3D2F] mb-1">Permitted contact</p>
                <p className="flex gap-4">
                  {ho.contact.phone && <span><Phone className="w-3.5 h-3.5 inline mr-1" />{ho.contact.phone}</span>}
                  {ho.contact.email && <span><Mail className="w-3.5 h-3.5 inline mr-1" />{ho.contact.email}</span>}
                </p>
              </div>
            )}

            <HandoffOutcome hid={hid} handoff={ho} reload={reload} />

            {/* Conversation: full transcript ONLY when the consumer recorded a share decision */}
            <div className="mt-1">
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare className="w-4 h-4 text-[#0B192C]" />
                <p className="text-sm font-bold text-[#0B192C]">Ask Nauticus conversation</p>
              </div>
              {ho.transcript_shared && ho.transcript ? (
                <div data-testid="journey-transcript">
                  {ho.transcript_consent?.text && (
                    <p className="text-[11px] text-[#166534] bg-[#DCFCE7] rounded px-2 py-1 mb-2" data-testid="journey-transcript-consent">
                      Shared by consumer consent · {ho.transcript_consent.recipient}{ho.transcript_consent.decided_at ? ` · ${(ho.transcript_consent.decided_at || "").slice(0, 10)}` : ""}
                    </p>
                  )}
                  <div className="space-y-2 max-h-[42vh] overflow-y-auto rounded-lg border border-[#E5E5E0] bg-[#FBFBF8] p-3">
                    {ho.transcript.map((m, i) => (
                      <div key={i} className={`flex ${m.role === "user" ? "justify-end" : ""}`} data-testid={`journey-transcript-msg-${i}`}>
                        <div className={`rounded-2xl px-3 py-2 text-sm max-w-[85%] ${m.role === "user" ? "bg-[#0B192C] text-white rounded-br-sm" : "bg-white border border-[#E5E5E0] text-[#0B192C] rounded-bl-sm"}`}>
                          {m.content}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div data-testid="journey-summary-only">
                  <p className="text-[11px] text-[#92400E] bg-[#FEF3C7] rounded px-2 py-1 mb-2">
                    Showing the authorized summary only — the consumer did not choose to share the full conversation.
                  </p>
                  <p className="text-sm text-[#374151] whitespace-pre-wrap rounded-lg border border-[#E5E5E0] bg-[#FBFBF8] p-3" data-testid="journey-summary-text">
                    {ho.conversation_context || "No conversation context was captured."}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* Facts */}
      <Card>
        <div className="flex items-center gap-2 mb-3"><ClipboardList className="w-4 h-4 text-[#0B192C]" /><h3 className="font-bold text-[#0B192C]">What Nauticus knows</h3></div>
        {facts.length === 0 ? (
          <p className="text-sm text-[#6B7280]" data-testid="journey-facts-empty">No structured facts captured yet.</p>
        ) : (
          <div className="space-y-2.5" data-testid="journey-facts">
            {facts.map((f) => (
              <div key={f.key} className="flex items-start justify-between gap-3 flex-wrap" data-testid={`journey-fact-${f.key}`}>
                <div className="min-w-0">
                  <p className="text-xs text-[#6B7280]">{f.label}</p>
                  <p className="font-semibold text-[#0B192C]">{f.value}</p>
                </div>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full mt-1 ${EVIDENCE_PILL[f.evidence_label] || "bg-[#F1F5F9] text-[#475569]"}`} data-testid={`journey-fact-evidence-${f.key}`}>{f.evidence_label}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Advisor eligibility confirmation */}
      <EligibilityConfirm ctx={ctx} hid={hid} reload={reload} />

      {/* Plans under investigation */}
      <Card>
        <div className="flex items-center gap-2 mb-3"><Compass className="w-4 h-4 text-[#0B192C]" /><h3 className="font-bold text-[#0B192C]">Plans under investigation</h3></div>
        {plans.length === 0 ? (
          <p className="text-sm text-[#6B7280]" data-testid="journey-plans-empty">No plans are currently in review.</p>
        ) : (
          <div className="space-y-2" data-testid="journey-plans">
            {plans.map((pl, i) => (
              <div key={i} className="rounded-lg border border-[#E5E5E0] p-3" data-testid={`journey-plan-${i}`}>
                <p className="font-semibold text-sm text-[#0B192C]">{pl.plan_name}</p>
                <p className="text-xs text-[#6B7280]">{[pl.carrier, pl.plan_type, pl.snp_type, pl.plan_year].filter(Boolean).join(" · ")}</p>
                {pl.subtitle && <p className="text-xs text-[#6B7280] mt-0.5">{pl.subtitle}</p>}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

const COND_OPTS = [
  { key: "diabetes", label: "Diabetes", canon: "diabetes" },
  { key: "heart", label: "Heart or circulatory", canon: "cardiovascular disorders" },
  { key: "lung", label: "COPD or chronic lung", canon: "chronic lung disorders" },
];

function EligibilityConfirm({ ctx, hid, reload }) {
  const ce = ctx.confirmed_eligibility || null;
  const medFact = (ctx.facts || []).find((f) => f.key === "medicaid");
  const condFact = (ctx.facts || []).find((f) => f.key === "conditions");
  const MED_TXT = { yes: "Has Medicaid", no: "No Medicaid", not_sure: "Not sure" };
  const [open, setOpen] = useState(false);
  const [med, setMed] = useState(ce?.medicaid_status || "");
  const [level, setLevel] = useState(ce?.medicaid_level || "");
  const [conds, setConds] = useState(() => {
    const canon = new Set(ce?.conditions || []);
    return COND_OPTS.filter((o) => canon.has(o.canon)).map((o) => o.key);
  });
  const [note, setNote] = useState(ce?.note || "");
  const [saving, setSaving] = useState(false);
  const toggle = (k) => setConds((p) => p.includes(k) ? p.filter((x) => x !== k) : [...p, k]);
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/agent/households/${hid}/eligibility-confirm`, {
        medicaid_status: med || null, medicaid_level: level || null, conditions: conds, note: note || null });
      toast.success("Eligibility confirmed");
      setOpen(false); await reload();
    } catch { toast.error("Couldn't save confirmation."); }
    finally { setSaving(false); }
  };
  return (
    <Card>
      <div className="flex items-center gap-2 mb-3"><ShieldCheck className="w-4 h-4 text-[#0B192C]" /><h3 className="font-bold text-[#0B192C]">Eligibility (Medicaid &amp; conditions)</h3></div>
      <div className="grid sm:grid-cols-2 gap-3" data-testid="eligibility-reported">
        <div><p className="text-xs text-[#6B7280]">Member reported — Medicaid</p><p className="font-semibold text-[#0B192C]">{medFact?.value || "Not reported"}</p></div>
        <div><p className="text-xs text-[#6B7280]">Member reported — conditions</p><p className="font-semibold text-[#0B192C]">{condFact?.value || "None reported"}</p></div>
      </div>
      {ce ? (
        <div className="mt-3 rounded-lg bg-[#EDE9FE] border border-[#DDD6FE] p-3" data-testid="eligibility-confirmed">
          <p className="text-xs font-bold text-[#5B21B6] uppercase tracking-wide">Advisor confirmed</p>
          <p className="text-sm text-[#0B192C] mt-1">Medicaid: <span className="font-semibold">{MED_TXT[ce.medicaid_status] || "—"}</span>{ce.medicaid_level ? ` · ${ce.medicaid_level}` : ""}</p>
          <p className="text-sm text-[#0B192C]">Conditions: <span className="font-semibold">{(ce.conditions || []).join(", ") || "None"}</span></p>
          {ce.note && <p className="text-xs text-[#6B7280] mt-1">Note: {ce.note}</p>}
          <p className="text-[11px] text-[#6B7280] mt-1">By {ce.confirmed_by?.name || ce.confirmed_by?.email || "advisor"} · {(ce.confirmed_at || "").slice(0, 16).replace("T", " ")}</p>
        </div>
      ) : (
        <p className="mt-3 text-sm text-[#6B7280]" data-testid="eligibility-unconfirmed">Not yet confirmed by an advisor. This is advisor-attested, not a live eligibility lookup.</p>
      )}
      <Button size="sm" variant="outline" className="mt-3" onClick={() => setOpen(true)} data-testid="eligibility-confirm-open"><Check className="w-4 h-4 mr-1.5" />{ce ? "Update confirmation" : "Confirm eligibility"}</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="eligibility-confirm-dialog">
          <DialogHeader><DialogTitle>Confirm member eligibility</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Florida Medicaid</Label>
              <div className="mt-1 flex flex-wrap gap-2">
                {[["yes", "Has Medicaid"], ["no", "No Medicaid"], ["not_sure", "Not sure"]].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setMed(v)} data-testid={`eligibility-med-${v}`}
                    className={`text-sm rounded-full px-3 py-1.5 font-semibold border ${med === v ? "bg-[#1E3E62] text-white border-[#1E3E62]" : "bg-white text-[#0B192C] border-[#D7D4CB]"}`}>{l}</button>
                ))}
              </div>
            </div>
            {med === "yes" && (
              <div>
                <Label className="text-xs">Medicaid level (optional)</Label>
                <Input value={level} onChange={(e) => setLevel(e.target.value)} placeholder="e.g. QMB+, SLMB, Full Medicaid" className="mt-1" data-testid="eligibility-level" />
              </div>
            )}
            <div>
              <Label className="text-xs">Chronic conditions</Label>
              <div className="mt-1 flex flex-wrap gap-2">
                {COND_OPTS.map((o) => (
                  <button key={o.key} type="button" onClick={() => toggle(o.key)} data-testid={`eligibility-cond-${o.key}`}
                    className={`text-sm rounded-full px-3 py-1.5 font-semibold border inline-flex items-center gap-1 ${conds.includes(o.key) ? "bg-[#1E3E62] text-white border-[#1E3E62]" : "bg-white text-[#0B192C] border-[#D7D4CB]"}`}>{conds.includes(o.key) && <Check className="w-3.5 h-3.5" />}{o.label}</button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Note (optional)</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Context for this confirmation (no SSN/MBI)." className="mt-1" data-testid="eligibility-note" />
            </div>
            <Button onClick={save} disabled={saving} className="w-full bg-[#0B192C] text-white hover:bg-[#1E3E62]" data-testid="eligibility-save">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}Save confirmation</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

const ANS_BADGE = {
  resolved: "bg-[#DCFCE7] text-[#166534]", not_present: "bg-[#FEF3C7] text-[#92400E]",
  not_applicable: "bg-[#F1F5F9] text-[#475569]", insufficient_detail: "bg-[#FEF3C7] text-[#92400E]",
  verification_required: "bg-[#E0F2FE] text-[#075985]", unknown: "bg-[#F1F5F9] text-[#475569]",
  unresolved: "bg-[#FEE2E2] text-[#991B1B]", ambiguous: "bg-[#FFEDD5] text-[#9A3412]",
  clarification: "bg-[#FFEDD5] text-[#9A3412]", missing_identity: "bg-[#FEE2E2] text-[#991B1B]",
  needs_provider_npi: "bg-[#FFEDD5] text-[#9A3412]", needs_pharmacy_identity: "bg-[#FFEDD5] text-[#9A3412]",
  unsupported: "bg-[#F1F5F9] text-[#475569]", plan_ambiguous: "bg-[#FEE2E2] text-[#991B1B]",
  plan_unresolved: "bg-[#FEE2E2] text-[#991B1B]",
};
const STATE_LABEL = {
  resolved: "Answer", not_present: "Not in dataset", not_applicable: "Not applicable",
  insufficient_detail: "Limited data", verification_required: "Verify with source",
  unknown: "Unavailable", unresolved: "Needs info", ambiguous: "Choose one",
  clarification: "Needs detail", missing_identity: "Verify plan first",
  needs_provider_npi: "Identify provider", needs_pharmacy_identity: "Identify pharmacy",
  unsupported: "Out of scope",
};

const STARTERS = (n) => {
  const s = n || "this member";
  return [
    { cat: "Costs", q: `What is ${s}'s monthly premium?` },
    { cat: "Prescriptions", q: "Is Eliquis covered?" },
    { cat: "Hospital & medical", q: `What does ${s}'s plan cover for an inpatient hospital stay?` },
    { cat: "Coverage", q: `What is ${s}'s specialist copay?` },
  ];
};

function firstName(name) {
  const nm = (name || "").trim();
  if (nm.includes("'s ")) return nm.split("'s ")[0].trim();
  if (nm.endsWith("'s Household")) return nm.slice(0, -"'s Household".length).trim();
  return nm.split(" ")[0] || "";
}

function Provenance({ prov }) {
  const [open, setOpen] = useState(false);
  if (!prov) return null;
  const entries = Object.entries(prov).filter(([, v]) => v !== null && v !== "");
  if (entries.length === 0) return null;
  return (
    <div className="mt-2">
      <button onClick={() => setOpen((o) => !o)} data-testid="copilot-provenance-toggle"
        className="text-xs text-[#1E3E62] font-semibold inline-flex items-center gap-1 hover:underline">
        <InfoIcon className="w-3 h-3" /> What Nauticus relied on
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <dl className="mt-1.5 grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5 text-[11px] bg-[#F8F8F5] border border-[#E5E5E0] rounded-lg p-2.5" data-testid="copilot-provenance-body">
          {entries.map(([k, v]) => (
            <React.Fragment key={k}>
              <dt className="text-[#6B7280] font-semibold whitespace-nowrap">{k.replace(/_/g, " ")}</dt>
              <dd className="text-[#0B192C] break-all">{String(v)}</dd>
            </React.Fragment>
          ))}
        </dl>
      )}
    </div>
  );
}

function CoPilot({ hid, householdName, county, usState, memberEmail }) {
  const [ctx, setCtx] = useState(null);
  const [turns, setTurns] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [npiDraft, setNpiDraft] = useState({});
  const [showCompare, setShowCompare] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [sharePayload, setSharePayload] = useState(null);
  const [shareEmail, setShareEmail] = useState("");
  const [sharing, setSharing] = useState(false);
  const threadRef = React.useRef(null);
  const subject = firstName(householdName) || "this member";
  const currentIdentity = (ctx?.state === "resolved" && ctx.contract_id) ? {
    contract_id: ctx.contract_id, pbp: ctx.pbp, segment_id: ctx.segment_id,
    display_id: `${ctx.contract_id}-${ctx.pbp}-${ctx.segment_id}`,
    plan_name: ctx.provenance?.organization_marketing_name || ctx.provenance?.plan_name_cms || `${ctx.contract_id}-${ctx.pbp}-${ctx.segment_id}`,
  } : null;
  const openShare = (p) => { setSharePayload(p); setShareEmail(memberEmail || ""); setShareOpen(true); };
  const sendShare = async () => {
    if (!shareEmail.trim()) { toast.error("Enter a recipient email"); return; }
    setSharing(true);
    try {
      const { data } = await api.post("/agent/compare/share", { household_id: hid, recipient_email: shareEmail.trim(), ...sharePayload });
      toast.success(data.delivered ? `Comparison emailed to ${shareEmail.trim()}` : "Secure link created (email delivery pending)");
      setShareOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't share the comparison"); }
    finally { setSharing(false); }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try { const { data } = await api.get(`/agent/households/${hid}/plan-context`); if (alive) setCtx(data); }
      catch { if (alive) setCtx({ state: "error" }); }
    })();
    return () => { alive = false; };
  }, [hid]);

  useEffect(() => { threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight, behavior: "smooth" }); }, [turns, busy]);

  const send = async (question, extraCtx) => {
    if (!question || busy) return;
    setBusy(true);
    setTurns((t) => [...t, { role: "advisor", text: question }]);
    setQ("");
    try {
      const body = { question };
      if (extraCtx) body.confirmed_context = extraCtx;
      const { data } = await api.post(`/agent/households/${hid}/copilot`, body);
      setTurns((t) => [...t, { role: "nauticus", question, answer: data.answer }]);
    } catch (e) {
      const msg = e?.response?.status === 403 ? "You don't have access to this household." : "Something went wrong — please try again.";
      setTurns((t) => [...t, { role: "nauticus", question, answer: { answer_state: "unresolved", answer: msg } }]);
    } finally { setBusy(false); }
  };

  const verified = ctx?.state === "resolved";
  return (
    <Card className="p-0 overflow-hidden">
      <div className="p-5 border-b border-[#E5E5E0] bg-[#0B192C] text-white">
        <div className="flex items-center gap-2">
          <Compass className="w-5 h-5 text-[#D4AF37]" />
          <h3 className="font-display font-bold text-lg" data-testid="copilot-header">Ask Nauticus about {subject}</h3>
          <button type="button" onClick={() => setShowCompare((v) => !v)} data-testid="copilot-compare-toggle"
            className="ml-auto text-xs font-bold bg-[#D4AF37] text-[#0B192C] px-3 py-1.5 rounded-full hover:bg-[#E6C14E]">
            {showCompare ? "Hide 2027 compare" : "Compare 2027 plans"}
          </button>
        </div>
        {ctx == null ? (
          <p className="text-xs text-mist-200 mt-2">Checking plan context…</p>
        ) : verified ? (
          <div className="mt-2 text-sm" data-testid="copilot-plan-context">
            <span className="text-mist-200">Current verified Medicare plan: </span>
            <span className="font-semibold text-white">{ctx.provenance?.organization_marketing_name || ctx.provenance?.plan_name_cms || "—"}</span>
            <span className="text-mist-200"> · {ctx.contract_id}-{ctx.pbp}-{ctx.segment_id} · {ctx.plan_year}</span>
          </div>
        ) : (
          <div className="mt-2 flex items-center justify-between gap-3 flex-wrap" data-testid="copilot-plan-context">
            <p className="text-sm text-[#F6C86B]">No verified CMS plan for this household yet — I can't answer plan-specific questions until it's verified.</p>
            <button onClick={() => document.querySelector('[data-testid="tab-coverage"]')?.click()}
              className="text-xs font-bold bg-[#D4AF37] text-[#0B192C] px-3 py-1.5 rounded-lg" data-testid="copilot-open-coverage">Open Coverage</button>
          </div>
        )}
      </div>

      {showCompare && (
        <div className="p-5 border-b border-[#E5E5E0] bg-[#FBFBF8]" data-testid="copilot-compare-section">
          <p className="text-xs text-[#6B7280] mb-3">Factual 2027 plan comparison for {subject} — same deterministic engine, in-context. Provider networks and drug coverage aren't included; this isn't a recommendation.</p>
          <PlanComparePanel variant="agent" defaultCounty={county || ""} defaultState={usState || "FL"} initialCurrent={currentIdentity} onShare={openShare} />
          <Dialog open={shareOpen} onOpenChange={setShareOpen}>
            <DialogContent data-testid="copilot-share-dialog">
              <DialogHeader><DialogTitle>Email this comparison to {subject}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Send to</Label>
                  <Input value={shareEmail} onChange={(e) => setShareEmail(e.target.value)} placeholder="member@email.com" className="mt-1" data-testid="copilot-share-email" />
                  <p className="text-[11px] text-[#8A8F98] mt-1">We'll email a secure link only — no personal details in the email. Changing this address here does <strong>not</strong> change the household's saved email.</p>
                </div>
                <Button onClick={sendShare} disabled={sharing || !shareEmail.trim()} className="w-full bg-[#0B192C] text-white rounded-full h-10" data-testid="copilot-share-send">
                  {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" /> Send secure link</>}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}

      <div ref={threadRef} className="p-5 space-y-4 max-h-[46vh] overflow-y-auto bg-[#FBFBF8]" data-testid="copilot-thread">
        {turns.length === 0 && (
          <div data-testid="copilot-starters">
            <p className="text-sm text-[#6B7280] mb-3">Ask about {subject}'s verified plan. Try one of these:</p>
            <div className="flex flex-wrap gap-2">
              {STARTERS(subject).map((s, i) => (
                <button key={i} onClick={() => send(s.q)} disabled={busy} data-testid={`copilot-starter-${i}`}
                  className="text-left rounded-full border border-[#E5E5E0] bg-white hover:border-[#D4AF37] hover:shadow-sm transition-all px-3.5 py-2">
                  <span className="block text-[10px] font-bold uppercase tracking-wide text-[#D4AF37]">{s.cat}</span>
                  <span className="block text-sm text-[#0B192C]">{s.q}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        {turns.map((t, i) => t.role === "advisor" ? (
          <div key={i} className="flex justify-end"><div className="bg-[#0B192C] text-white rounded-2xl rounded-br-sm px-4 py-2.5 text-sm max-w-[80%] shadow-sm" data-testid={`copilot-advisor-${i}`}>{t.text}</div></div>
        ) : (
          <div key={i} className="flex" data-testid={`copilot-answer-${i}`}>
            <div className="bg-white border border-[#E5E5E0] rounded-2xl rounded-bl-sm px-4 py-3.5 text-sm max-w-[90%] shadow-sm">
              <span className={`inline-block text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded mb-2 ${ANS_BADGE[t.answer.answer_state] || "bg-[#F1F5F9] text-[#475569]"}`} data-testid={`copilot-state-${i}`}>{STATE_LABEL[t.answer.answer_state] || t.answer.answer_state}</span>
              <p className="text-[#0B192C] leading-relaxed whitespace-pre-wrap">{t.answer.answer}</p>

              {t.answer.action?.type === "open_coverage_tab" && (
                <button onClick={() => document.querySelector('[data-testid="tab-coverage"]')?.click()}
                  className="mt-3 text-xs font-bold bg-[#0B192C] text-white px-3 py-1.5 rounded-lg" data-testid={`copilot-action-coverage-${i}`}>Open Coverage</button>
              )}

              {t.answer.resolution?.options?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid={`copilot-choices-${i}`}>
                  {t.answer.resolution.options.map((o, oi) => (
                    <button key={oi} onClick={() => send(t.question, { [t.answer.resolution.param]: o.value })} disabled={busy}
                      data-testid={`copilot-choice-${i}-${oi}`}
                      className="rounded-full border border-[#0B192C] hover:bg-[#0B192C] hover:text-white transition-colors px-3 py-1.5 text-sm">
                      {o.label}{o.sublabel ? <span className="opacity-60 ml-1 text-xs">{o.sublabel}</span> : null}
                    </button>
                  ))}
                  {t.answer.resolution.allow_unsure && (
                    <button onClick={() => setTurns((tt) => [...tt, { role: "nauticus", question: t.question, answer: { answer_state: "clarification", answer: `No problem — type the exact name, strength, and form (e.g. "Eliquis 5 mg tablet") and I'll check it.` } }])}
                      disabled={busy} data-testid={`copilot-choice-${i}-unsure`}
                      className="rounded-full border border-[#E5E5E0] text-[#6B7280] hover:border-[#0B192C] px-3 py-1.5 text-sm">I'm not sure</button>
                  )}
                </div>
              )}

              {t.answer.resolution?.input && (
                <div className="mt-3 flex gap-2" data-testid={`copilot-npi-${i}`}>
                  <Input value={npiDraft[i] || ""} onChange={(e) => setNpiDraft((d) => ({ ...d, [i]: e.target.value }))}
                    placeholder={t.answer.resolution.input_label || "NPI"} className="h-9 bg-white text-sm font-mono" data-testid={`copilot-npi-input-${i}`} />
                  <Button size="sm" className="bg-[#0B192C] text-white" disabled={busy || !(npiDraft[i] || "").trim()}
                    onClick={() => send(t.question, { [t.answer.resolution.param]: (npiDraft[i] || "").trim() })} data-testid={`copilot-npi-submit-${i}`}>Verify</Button>
                </div>
              )}

              <Provenance prov={t.answer.provenance} />
            </div>
          </div>
        ))}
        {busy && <div className="flex items-center gap-2 text-sm text-[#6B7280]" data-testid="copilot-thinking"><Loader2 className="w-4 h-4 animate-spin" /> Nauticus is checking the plan…</div>}
      </div>

      <div className="p-4 border-t border-[#E5E5E0] bg-white flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send(q)}
          placeholder={`Ask about ${subject}'s plan…`} className="bg-white" disabled={busy} data-testid="copilot-input" />
        <Button onClick={() => send(q)} disabled={busy || !q.trim()} className="bg-[#0B192C] text-white" data-testid="copilot-send">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </Card>
  );
}
